import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Box, Button, Fab, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  supportAgentApi,
  ChatDoc,
  ChatStatus,
  ChatTurn,
  CHAT_MAX_BODY_CHARS,
  CHAT_MAX_TURNS,
} from '../../pages/app/support-agent/support-agent-api';
import type { ChatAttachment, ChatAttachmentInput, ChatFeedback, ChatFeedbackInput } from '../../pages/app/support-agent/support-agent-api';
import {
  AttachmentList,
  AttachmentPickerButton,
  PendingAttachmentList,
  usePendingAttachments,
} from '../../pages/app/support-agent/chat-attachments';
import { SafeMarkdown } from '../../pages/app/support-agent/safe-markdown';
import { useEffectiveSettings, SETTING } from '../../hooks/use-effective-settings';
import { isAiChatEnabled } from './ai-chat-flag';
import { useLiveChatEvent, useLiveChatSocket } from '../../pages/app/support-agent/live-chat-socket';
import {
  useAiChatIntro,
  AnnouncementDialog,
  TourOverlay,
  NudgeLabel,
  AI_CHAT_INTRO_SHOW_AGAIN,
  RobotIcon,
  POM_PULSE_KEYFRAMES,
} from './ai-chat-intro';
import { supportHoursState } from './ai-chat-support-hours';
import { AiChatFeedback } from './ai-chat-feedback';
import {
  ConfirmHumanSheet,
  ConfirmRestartSheet,
  ConfirmResolveSheet,
  HandoffBanner,
  resolvedCopy,
  EmergencyCard,
  AI_CHAT_EMERGENCY_BODY,
} from './ai-chat-escalation';

/**
 * Customer-facing chat with the support agent.
 *
 * Renders a floating button in the bottom-right of the logged-in dashboard
 * shell (mounted from layout/dashboard/index.tsx) and, when clicked, a chat
 * panel backed by the PERSISTED chat (Ticket "AI chat escalation v1", 9/9) —
 * `POST /support-agent/chat` et al. (chat.service.ts). Every message here is
 * saved server-side, including a POM Master's own practice chats (Carlos,
 * 9/9) — they all show up in the POM Master "Chats" inbox (chats-panel.tsx).
 * The endpoint just requires a logged-in webapp user (verifyJwt; owner-scoped
 * in chat.service.ts) — WHO actually sees this button is a frontend decision
 * made in AiChatWidget below: a POM Master's own session always does
 * (unchanged since this was POM-Master-only); a real customer, OR a
 * masquerading session (Carlos, 9/14 — deliberately writes into that
 * customer's actual persisted chat, same as the customer's own session
 * would), does when their org's `chatSupport.mode` setting (Carlos, 9/11) is
 * `ai_chat_only` or `both`.
 *
 * Gated end to end by REACT_APP_AI_CHAT_ENABLED (ai-chat-flag.ts). With the
 * flag off this component returns null before any hook runs, so no request is
 * ever made.
 *
 * The Zendesk Messaging launcher is left exactly as it is. This button stacks
 * ABOVE it in the same corner so the customer can pick a person (Zendesk) or
 * the assistant (this) — the whole point of a separate button.
 *
 * No AI-triggered escalation in this version (Carlos, 9/9) — "Talk to a
 * person" is the only way a chat becomes `escalated`. Once escalated, the AI
 * stops replying (server-enforced; see chat.service.ts) but the customer can
 * keep typing, and a 5s poll (Carlos, 9/9: no WebSockets) picks up whatever a
 * POM Master types back from the "Chats" inbox.
 *
 * Minimize + drag (Carlos, 9/11): the bottom Fab, while the panel is open,
 * minimizes rather than resolving — the chat stays live in the background and
 * the poll keeps running so a badge can surface a human reply or a status
 * change the customer hasn't seen yet. The header X is now the only way to
 * actually end a chat (still asks first via the same confirm dialog). The
 * panel itself can be dragged by its header to anywhere on screen; the
 * position persists across minimize/reopen/refresh, and the Fab never moves.
 */

export const AI_CHAT_TITLE = 'POM Safe Assistant';
export const AI_CHAT_EMPTY_COPY = 'Hi! Ask me anything about your POM device, the app, or your account.';
// The exact body text shown in the EmergencyCard (ai-chat-escalation.tsx) —
// kept as a single source of truth so displayedTurnText stays byte-identical
// to what the customer actually saw (see the AgentTurn doc comment below).
export const AI_CHAT_URGENT_COPY = AI_CHAT_EMERGENCY_BODY;
export const AI_CHAT_SKIPPED_COPY =
  "I'm sorry, I can't help with that here. If you have a question about your POM device or account, I'm happy to help.";
export const AI_CHAT_NO_ANSWER_COPY = "I couldn't come up with an answer for that. Could you try rephrasing?";
export const AI_CHAT_ERROR_COPY = "Something went wrong and I couldn't reply. Please try again.";
// Terminal, not retryable (pom-2-api chat.service.ts refuses the message up
// front with SUPPORT_AGENT_HISTORY_LIMIT when the chat's stored retrieval
// history exceeds the Lambda's transport cap — PR 2, 9/11). Resending the same
// message cannot fix it, so the only offered action is "Talk to a person";
// the typed text is put back in the box so nothing the customer wrote is lost.
export const AI_CHAT_HISTORY_LIMIT_COPY =
  "This chat has grown past what I can keep track of, so I can't answer here. Talk to a person and they'll pick it up with your message.";
export const AI_CHAT_HISTORY_LIMIT_CODE = 'SUPPORT_AGENT_HISTORY_LIMIT';
export const AI_CHAT_RATE_LIMITED_COPY = "You're sending messages a little too fast. Please wait a few seconds and try again.";
export const AI_CHAT_THINKING_COPY = 'Thinking…';
// A POM Master typing after escalation (chat_typing over the live socket):
// "Derek is typing", or the generic staff label when the server sends no name.
export const AI_CHAT_STAFF_NAME = 'Support';
export const staffTypingCopy = (name: string) => `${name} is typing`;
// How long one chat_typing keeps the dots up with no renewal (the server
// sends one every few seconds while the agent types, and typing:false when
// they stop; a reply landing clears it too).
export const AI_CHAT_STAFF_TYPING_TTL_MS = 6000;
// "Customer is typing" for the Chats inbox: at most one note this often.
export const CUSTOMER_TYPING_SEND_INTERVAL_MS = 3000;
export const AI_CHAT_ESCALATE_ERROR_COPY = "Couldn't reach a person just now. Please try again.";
export const AI_CHAT_RESOLVE_ERROR_COPY = "Couldn't resolve this chat. Please try again.";

// How often the open panel polls for new turns (a human reply, or a status
// change) once a chat exists. Carlos, 9/9: poll, no WebSockets — and since
// 10/8, with the live socket connected (live-chat-socket.ts), the poll is
// only a slow safety net behind the server's chat_update doorbells.
const CHAT_POLL_INTERVAL_MS = 5000;
export const CHAT_POLL_FALLBACK_INTERVAL_MS = 30_000;

// The chat's status only ever moves forward (Pollen review, round 3 — "Talk
// to a person is enabled during a send, so the send's response can flip
// status back to ai after escalation lands"): a send and an escalate can
// both be in flight, and their responses can arrive out of order regardless
// of which the server processed first. sendToApi uses this to ignore a
// response that would move status BACKWARD (e.g. a stale 'ai' arriving
// after an escalate already landed 'escalated') without discarding a
// legitimate escalate or resolve that's still ahead of it.
const CHAT_STATUS_RANK: Record<ChatStatus, number> = { ai: 0, escalated: 1, resolved: 2 };

// Bottom-right placement, stacked above the Zendesk Messaging launcher, which
// sits in the same corner. Measured defaults of the Zendesk widget: a 64px
// round launcher (iframe#launcher) inset 16px from the bottom and right
// edges, and — until the customer dismisses it — a ~46px greeting label
// ("Hi. Need any help?") that sits just above the launcher and extends to the
// left. Rather than reserving space for the greeting forever, we measure what
// Zendesk is actually showing (useZendeskStack) and sit directly above it:
// above the greeting while it's up, dropping down to the launcher when the
// customer closes it, and all the way to the corner if Zendesk never loads.
const FAB_SIZE_PX = 56;
// Nudge dot (design: nudge.markup.html line 40): 14px, 2px inset from the
// Fab's own top-right corner.
const NUDGE_DOT_SIZE_PX = 14;
const NUDGE_DOT_INSET_PX = 2;
const ZENDESK_LAUNCHER_SIZE_PX = 64;
const ZENDESK_LAUNCHER_INSET_PX = 16;
const STACK_GAP_PX = 12;
const EDGE_OFFSET_PX = ZENDESK_LAUNCHER_INSET_PX + (ZENDESK_LAUNCHER_SIZE_PX - FAB_SIZE_PX) / 2; // 20
const PANEL_WIDTH_PX = 380;
const PANEL_HEIGHT_PX = 560;
const PANEL_ID = 'ai-chat-panel';
// MUI's Dialog defaults to theme.zIndex.modal (1300). Only override the
// announcement dialog's z-index when Zendesk is actually stacked above that.
const MUI_DIALOG_Z_INDEX = 1300;

// Drag (Carlos, 9/11): once the customer drags the panel, its position is
// pinned by left/top and survives minimize/reopen/refresh via localStorage.
// Undragged is the default — today's Zendesk-stack-relative placement above.
export const PANEL_POSITION_STORAGE_KEY = 'pomsafe.aiChat.panelPosition';

// Per-user key (Interrogator finding, 2026-09-11 round 2 — "drag position is
// one global localStorage key, not per-user"): more than one POM Master
// signing into the same browser/machine would otherwise inherit whoever
// dragged the panel last.
export function panelPositionStorageKey(userId: string | undefined): string {
  return userId ? `${PANEL_POSITION_STORAGE_KEY}.${userId}` : PANEL_POSITION_STORAGE_KEY;
}

export interface PanelPosition {
  left: number;
  top: number;
}

/**
 * Clamps a candidate panel position so the WHOLE panel — not just its
 * header — stays inside the viewport, on every drag move, on load (a window
 * resized smaller since the position was saved), and on a live window
 * resize. Pure so it's directly testable without mounting anything.
 *
 * Deliberately does NOT keep the panel below the app's own AppBar — dragging
 * over the app chrome, including the nav, is intentional (Carlos, 9/11: "the
 * nav must not render on top of it" is the whole reason this panel's zIndex
 * sits above both the AppBar and the Drawer below).
 */
export function clampPanelPosition(left: number, top: number, viewportWidth: number, viewportHeight: number): PanelPosition {
  const maxLeft = Math.max(0, viewportWidth - PANEL_WIDTH_PX);
  const maxTop = Math.max(0, viewportHeight - PANEL_HEIGHT_PX);
  return { left: Math.min(Math.max(0, left), maxLeft), top: Math.min(Math.max(0, top), maxTop) };
}

function readStoredPanelPosition(userId: string | undefined): PanelPosition | null {
  try {
    const raw = window.localStorage.getItem(panelPositionStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return { left: parsed.left, top: parsed.top };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredPanelPosition(userId: string | undefined, position: PanelPosition): void {
  try {
    window.localStorage.setItem(panelPositionStorageKey(userId), JSON.stringify(position));
  } catch {
    // Ignore storage write failures — the position just won't survive a refresh.
  }
}

/**
 * The text a turn actually shows, in every shape a persisted turn can take:
 * a real draft, the empty-draft fallback, urgent, skipped, or a human reply.
 * Branches on `urgent`/`skipped`, never on `body`'s emptiness alone — an
 * urgent or skipped agent turn is stored with `body: ''` server-side (see
 * pom-2-api chat.service.ts postMessage), so this is the only correct way to
 * derive display text from a turn.
 */
export function displayedTurnText(turn: ChatTurn): string {
  if (turn.role === 'agent') {
    if (turn.urgent) return AI_CHAT_URGENT_COPY;
    if (turn.skipped) return AI_CHAT_SKIPPED_COPY;
    if (!turn.body) return AI_CHAT_NO_ANSWER_COPY;
    return turn.body;
  }
  return turn.body;
}

export interface ZendeskStack {
  /** Height of the Zendesk stack in the bottom-right corner, in px from the viewport bottom. 0 when Zendesk isn't showing anything. */
  topPx: number;
  /** Highest z-index among the launcher, any other visible Zendesk iframe, and their ancestors. null when none of them set one. */
  zIndex: number | null;
}

interface RectLike {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Pure measurement: given the on-screen rectangles of the Zendesk launcher
 * and every other visible Zendesk iframe, how high does the Zendesk stack
 * reach? The greeting label and its close button are separate iframes that
 * Zendesk positions just above the launcher; anything with real size whose
 * bottom edge sits within one launcher-height above the launcher's top counts
 * as part of the stack. Position-based on purpose: Zendesk's iframe titles
 * are localized and undocumented, so matching on them would be brittle.
 */
export function measureZendeskStack(
  viewportHeight: number,
  launcher: RectLike | null,
  others: RectLike[],
): { topPx: number } {
  if (!launcher || launcher.height === 0) return { topPx: 0 };
  let top = launcher.top;
  for (const r of others) {
    if (r.width === 0 || r.height === 0) continue;
    const sitsAboveLauncher = r.bottom <= launcher.top + 1 && r.bottom >= launcher.top - ZENDESK_LAUNCHER_SIZE_PX * 1.5;
    const inTheCorner = r.right >= launcher.left;
    if (sitsAboveLauncher && inTheCorner) top = Math.min(top, r.top);
  }
  return { topPx: Math.max(0, Math.round(viewportHeight - top)) };
}

/**
 * `getBoundingClientRect` keeps reporting an iframe's laid-out size even
 * after `zE('messenger','hide')` hides it — Zendesk sets `visibility:
 * hidden; opacity: 0` on the launcher's wrapper div, not the iframe, so the
 * iframe's own rect and inline style never change. Without this check a
 * hidden launcher still measures as present, stacking the AI button above
 * an empty corner.
 */
function isZendeskElementVisible(el: Element): boolean {
  const withCheckVisibility = el as Element & { checkVisibility?: (opts?: Record<string, boolean>) => boolean };
  if (typeof withCheckVisibility.checkVisibility === 'function') {
    return withCheckVisibility.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  }
  // jsdom (tests) has no checkVisibility — walk the ancestor chain instead,
  // since opacity/visibility set on a wrapper isn't visible on the iframe's
  // own computed style.
  let node: Element | null = el;
  while (node) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) {
      return false;
    }
    node = node.parentElement;
  }
  return true;
}

/**
 * Highest numeric z-index set on `el` or any of its ancestors, or null if
 * none of them set one. An unset z-index computes as `''` (not `'auto'`) in
 * some engines, and `Number('')` is `0`, not `NaN` — so `''` and `'auto'`
 * are both treated as absent here rather than silently counting as 0, which
 * would make every plain element "outrank" a real override of 0 or less.
 */
function highestZIndex(el: Element): number | null {
  let node: Element | null = el;
  let highest: number | null = null;
  while (node) {
    const raw = window.getComputedStyle(node).zIndex;
    const z = raw === '' || raw === 'auto' ? NaN : Number(raw);
    if (!Number.isNaN(z)) highest = highest === null ? z : Math.max(highest, z);
    node = node.parentElement;
  }
  return highest;
}

/**
 * At narrow widths a visible Zendesk iframe (the launcher, or the "Hi. Need
 * any help?" greeting bubble) overlaps the announcement dialog, and since
 * Zendesk stacks them at a z-index of its own choosing (observed well above
 * MUI's Dialog default of 1300), `elementFromPoint` on "Maybe later" / "Try
 * it now" resolves to the iframe instead of the dialog underneath it. Read
 * the real highest z-index across the same visible iframes (and their
 * ancestors, since Zendesk sets it on a wrapper rather than the iframe
 * itself) this already measures for the stack height, instead of hardcoding
 * a number Zendesk is free to change.
 */
export function readZendeskStack(): ZendeskStack {
  if (typeof document === 'undefined') return { topPx: 0, zIndex: null };
  const launcherEl = document.getElementById('launcher');
  const launcherVisible = launcherEl instanceof HTMLIFrameElement && isZendeskElementVisible(launcherEl);
  const launcher = launcherVisible ? (launcherEl as HTMLIFrameElement).getBoundingClientRect() : null;
  const otherEls = Array.from(document.querySelectorAll('iframe')).filter(
    (f) => f !== launcherEl && isZendeskElementVisible(f),
  );
  const visibleEls = launcherVisible ? [launcherEl as Element, ...otherEls] : otherEls;
  const zIndex = visibleEls.reduce<number | null>((highest, el) => {
    const z = highestZIndex(el);
    if (z === null) return highest;
    return highest === null ? z : Math.max(highest, z);
  }, null);
  return {
    ...measureZendeskStack(window.innerHeight, launcher, otherEls.map((f) => f.getBoundingClientRect())),
    zIndex,
  };
}

/**
 * Tracks how much of the bottom-right corner Zendesk is using so the AI
 * button can sit right on top of it, and the highest z-index it's currently
 * using so the first-run announcement dialog can sit above that too. Zendesk
 * mounts its iframes async, fades them in, and collapses the greeting when
 * the customer dismisses it — and it does that through a mix of DOM
 * insertion, attribute changes and internal layout, not one reliable signal.
 * So we listen to all of them: any DOM or attribute mutation under <body>,
 * size changes of every iframe (ResizeObserver, re-attached whenever iframes
 * are added), window resizes, and a slow 2s poll as the backstop — the same
 * triggers cover a launcher that is still loading on a fresh login and only
 * mounts after this first measurement. Measurements are debounced to one per
 * frame and state only changes when a value does, so the poll is free when
 * nothing moves.
 */
function useZendeskStack(): ZendeskStack {
  const [stack, setStack] = useState<ZendeskStack>({ topPx: 0, zIndex: null });

  useEffect(() => {
    let frame: number | null = null;
    let disposed = false;
    const apply = (next: ZendeskStack) => {
      if (disposed) return;
      setStack((prev) => (prev.topPx === next.topPx && prev.zIndex === next.zIndex ? prev : next));
    };
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        apply(readZendeskStack());
      });
    };

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    const watchIframes = () => {
      if (!resizeObserver) return;
      resizeObserver.disconnect();
      document.querySelectorAll('iframe').forEach((f) => resizeObserver.observe(f));
    };

    apply(readZendeskStack());
    watchIframes();
    const mutationObserver = new MutationObserver((records) => {
      if (records.some((r) => r.type === 'childList')) watchIframes();
      schedule();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', schedule);
    const poll = window.setInterval(schedule, 2000);

    return () => {
      disposed = true;
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      window.clearInterval(poll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return stack;
}

type RefreshAttachment = (attachment: ChatAttachment) => Promise<ChatAttachment | undefined>;

// A customer turn: text, files, or both (chat attachments, Michael 10/14).
// A files-only turn shows the files alone — never an empty text bubble.
const CustomerBubble: React.FC<{ body: string; attachments?: ChatAttachment[]; onRefreshAttachment?: RefreshAttachment }> = ({
  body,
  attachments,
  onRefreshAttachment,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
    <Paper
      elevation={0}
      sx={{ px: 1.5, py: 1, maxWidth: '80%', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}
    >
      {body && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: attachments?.length ? 1 : 0 }}>
          {body}
        </Typography>
      )}
      {attachments && attachments.length > 0 && (
        <AttachmentList attachments={attachments} refresh={onRefreshAttachment} align="flex-end" />
      )}
    </Paper>
  </Box>
);

const AssistantBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
    <Paper elevation={0} sx={{ px: 1.5, py: 1, maxWidth: '85%', bgcolor: 'action.hover', borderRadius: 2 }}>
      {children}
    </Paper>
  </Box>
);

/**
 * One non-customer turn — either the AI (`role: 'agent'`) or a POM Master
 * who took over after escalation (`role: 'human'`). The text always comes
 * from displayedTurnText for agent turns, so what is rendered here is
 * byte-identical to what a customer actually saw. A human turn is plain
 * typed text (never markdown-rendered) — nothing about it needs sanitizing
 * or formatting the way an LLM draft might.
 */
const AgentTurn: React.FC<{ turn: ChatTurn; onRefreshAttachment?: RefreshAttachment }> = ({ turn, onRefreshAttachment }) => {
  if (turn.role === 'human') {
    return (
      <AssistantBubble>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 0.25 }}>
          Support
        </Typography>
        {turn.body && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: turn.attachments?.length ? 1 : 0 }}>
            {turn.body}
          </Typography>
        )}
        {turn.attachments && turn.attachments.length > 0 && (
          <AttachmentList attachments={turn.attachments} refresh={onRefreshAttachment} />
        )}
      </AssistantBubble>
    );
  }

  const text = displayedTurnText(turn);
  if (turn.urgent) {
    return <EmergencyCard />;
  }
  if (turn.skipped || !turn.body) {
    return (
      <AssistantBubble>
        <Typography variant="body2">{text}</Typography>
      </AssistantBubble>
    );
  }
  return (
    <AssistantBubble>
      {/* The markdown renderer paints its own white canvas (`.wmde-markdown`),
          which shows as a white block inside the gray bubble — the admin
          panel never hit this because its bubbles are white. Make it inherit
          the bubble's background and text color instead. */}
      <Box
        data-color-mode="light"
        sx={{
          fontSize: 14,
          '& .wmde-markdown': { backgroundColor: 'transparent', color: 'inherit', fontSize: 'inherit' },
          '& .wmde-markdown p:last-child': { marginBottom: 0 },
        }}
      >
        <SafeMarkdown source={text} />
      </Box>
    </AssistantBubble>
  );
};

// Three dots pulsing in sequence — the standard messaging-app "typing"
// bubble (Carlos, 9/10), replacing the spinner + "Thinking…" caption.
// AI_CHAT_THINKING_COPY moves to a visually-hidden text node: the enclosing
// transcript's aria-live="polite" region still announces it to screen
// readers, and existing tests that query for it keep working unchanged.
const TYPING_DOT_KEYFRAMES = {
  '0%, 80%, 100%': { opacity: 0.3 },
  '40%': { opacity: 1 },
};

const TypingIndicator: React.FC<{ label?: string }> = ({ label }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }} aria-label={label ?? 'Assistant is typing'}>
    <Paper elevation={0} sx={{ px: 1.5, py: 1.25, bgcolor: 'action.hover', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'ai-chat-typing-dot 1.2s infinite',
              animationDelay: `${i * 0.2}s`,
              '@keyframes ai-chat-typing-dot': TYPING_DOT_KEYFRAMES,
            }}
          />
        ))}
      </Box>
      <Typography component="span" sx={visuallyHidden}>
        {label ?? AI_CHAT_THINKING_COPY}
      </Typography>
    </Paper>
  </Box>
);

type ClosedReason = 'resolved' | 'limit';

const CLOSED_FOOTER_COPY: Record<ClosedReason, string> = {
  resolved: 'This chat is closed.',
  limit: 'This chat has reached its length limit.',
};

/**
 * Replaces the input row entirely once the chat can't take another message
 * (resolved, or hit the turn cap) — a grayed-out footer instead of a text
 * field/send button that LOOK live but silently do nothing (Carlos, 9/9:
 * the disabled-but-still-white input read as broken, not closed). One
 * component for both reasons, since the only thing that differs is the
 * caption.
 */
const ClosedFooter: React.FC<{ reason: ClosedReason; onStartNewChat: () => void }> = ({
  reason,
  onStartNewChat,
}) => (
  <Box sx={{ bgcolor: 'action.disabledBackground', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {CLOSED_FOOTER_COPY[reason]}
    </Typography>
    <Button variant="contained" fullWidth onClick={onStartNewChat}>
      Start a new chat
    </Button>
  </Box>
);

const AiChatWidgetInner: React.FC = () => {
  // Namespaces the dragged panel position per user (see
  // panelPositionStorageKey) — the outer `AiChatWidget` gate already reads
  // this same selector to decide whether to render at all.
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const zendesk = useZendeskStack();
  // Sit on top of whatever Zendesk is showing; in the corner if nothing is.
  const fabBottomPx = zendesk.topPx > 0 ? zendesk.topPx + STACK_GAP_PX : EDGE_OFFSET_PX;

  const [open, setOpen] = useState(false);
  const intro = useAiChatIntro(authUser?.id, open);
  const composerInputRef = useRef<HTMLInputElement | null>(null);
  const handleTryItNow = () => {
    intro.dismissAnnouncement();
    setOpen(true);
  };
  const handleTourNext = () => {
    const finishing = intro.tourStep === 3;
    intro.tourNext();
    if (finishing) {
      // Let the overlay unmount first so focus lands on the now-visible composer.
      requestAnimationFrame(() => composerInputRef.current?.focus());
    }
  };
  const [input, setInput] = useState('');
  const [transcript, setTranscript] = useState<ChatTurn[]>([]);
  const [status, setStatus] = useState<ChatStatus>('ai');
  const [sending, setSending] = useState(false);
  // Client-side cap violations — shown near the input, never in the transcript.
  const [validationError, setValidationError] = useState<string | null>(null);
  // Network/API failure — shown as a retryable message in the transcript. The
  // customer message that triggered it stays (it was appended optimistically),
  // so Retry re-issues the same request rather than re-appending anything.
  const [sendError, setSendError] = useState<string | null>(null);
  // See AI_CHAT_HISTORY_LIMIT_COPY: a terminal state for this chat until the
  // customer escalates or starts a new chat. Disables the composer so the
  // same oversized request is never presented as something a retry can fix.
  const [historyLimit, setHistoryLimit] = useState(false);
  // True only for a 429 (Pollen review, finding #9) — the generic Retry
  // action is hidden for this case since the send would just re-trip the
  // same rate limit; the customer needs to wait, not tap a button.
  const [sendRateLimited, setSendRateLimited] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  // "Talk to a person?" confirm sheet (Carlos, 10/7) — opened from the header
  // headset button AND from the chat-too-long warning's "Talk to a person"
  // button; handleEscalate itself only runs once the customer confirms.
  const [showConfirmHuman, setShowConfirmHuman] = useState(false);

  // Unread tracking for the minimized Fab (Carlos, 9/11 — server-persisted:
  // a client-side-only version forgot everything on a page refresh). The
  // server stamps `lastSeenAt` on the chat (POST /chat/:id/seen); the
  // customer's own unread state is however many agent/human turns — or a
  // status change — landed AFTER it. `escalatedAt`/`resolvedAt` mirror the
  // chat doc's own `escalation.at`/`resolvedAt` fields, updated alongside
  // `status` at every call site that sets it, so a status change can be
  // judged against `lastSeenAt` by an actual timestamp, the same way a new
  // turn is — not by remembering "did I already see this status" locally.
  const [lastSeenAt, setLastSeenAt] = useState<string | undefined>(undefined);
  const [escalatedAt, setEscalatedAt] = useState<string | undefined>(undefined);
  const [resolvedAt, setResolvedAt] = useState<string | undefined>(undefined);
  // Who/what resolved the chat (ChatDoc.resolvedBy) — drives which resolved
  // copy shows (see ai-chat-escalation.tsx resolvedCopy). Kept alongside
  // resolvedAt, set at every call site that sets it, reset by handleNewChat.
  const [resolvedBy, setResolvedBy] = useState<ChatDoc['resolvedBy']>(undefined);
  // Feedback card (Michael, 10/20; pom-2-api #597): both come from the
  // server on every chat response — `feedbackEligible` is its "show the
  // card now" decision, `feedback` the stored answers (the card locks on
  // it). A resolved chat is no longer polled or on the socket, so the
  // locked card comes from the submit's own response, not a refetch.
  const [feedbackEligible, setFeedbackEligible] = useState(false);
  const [feedback, setFeedback] = useState<ChatFeedback | undefined>(undefined);
  const applyFeedback = (chat: ChatDoc) => {
    setFeedbackEligible(chat.feedbackEligible === true);
    setFeedback(chat.feedback);
  };
  const lastSeenTime = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;
  const unreadTurnCount = transcript.filter(
    (turn) => turn.role !== 'customer' && new Date(turn.at).getTime() > lastSeenTime,
  ).length;
  const statusChangedSinceSeen =
    (Boolean(escalatedAt) && new Date(escalatedAt!).getTime() > lastSeenTime) ||
    (Boolean(resolvedAt) && new Date(resolvedAt!).getTime() > lastSeenTime);
  const unreadCount = unreadTurnCount + (statusChangedSinceSeen ? 1 : 0);
  // True only when the chat resolved WHILE MINIMIZED and hasn't been shown
  // yet — the reopen effect further down must not wipe the transcript in
  // that case (Carlos, 9/11: "resolved while minimized" would otherwise lose
  // the conversation before the customer ever sees the resolved notice).
  const resolvedUnseen = status === 'resolved' && Boolean(resolvedAt) && new Date(resolvedAt!).getTime() > lastSeenTime;

  // Drag (Carlos, 9/11) — see PANEL_POSITION_STORAGE_KEY/clampPanelPosition
  // above. `null` means never dragged: the panel keeps today's placement,
  // anchored above the Fab/Zendesk stack.
  const [dragPosition, setDragPosition] = useState<PanelPosition | null>(() => {
    const stored = readStoredPanelPosition(authUser?.id);
    if (!stored) return null;
    return clampPanelPosition(stored.left, stored.top, window.innerWidth, window.innerHeight);
  });
  useEffect(() => {
    const onResize = () => {
      setDragPosition((current) =>
        current ? clampPanelPosition(current.left, current.top, window.innerWidth, window.innerHeight) : current,
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mouse events, not Pointer Events: this is a mouse-driven admin panel
  // (mobile is explicitly out of scope), and plain window-level
  // mousemove/mouseup — rather than pointer capture on the header itself —
  // means the drag keeps tracking even if the cursor leaves the header
  // mid-move. `onMove`/`onUp` are local to this one call, created fresh per
  // mousedown, so add/remove always target the exact same closures
  // regardless of any re-render in between (no ref bookkeeping needed).
  //
  // `dragListenersRef` holds whichever pair is currently attached, so an
  // unmount can remove them (Interrogator finding, 2026-09-11 round 2 — "drag
  // listeners are never cleaned up on unmount"): they're added directly here,
  // not inside a `useEffect`, and were previously only ever removed by `onUp`
  // firing — a component unmounting mid-drag (e.g. masquerade toggling
  // `AiChatWidget` to `null`) left them attached to `window` indefinitely.
  const dragListenersRef = useRef<{ onMove: (e: MouseEvent) => void; onUp: () => void } | null>(null);
  useEffect(() => {
    return () => {
      if (dragListenersRef.current) {
        window.removeEventListener('mousemove', dragListenersRef.current.onMove);
        window.removeEventListener('mouseup', dragListenersRef.current.onUp);
        dragListenersRef.current = null;
      }
    };
  }, []);

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Header buttons (Talk to a person / New chat / Close) must not start a
    // drag — only the bare header bar itself does.
    if ((e.target as HTMLElement).closest('button')) return;
    const panelEl = document.getElementById(PANEL_ID);
    const rect = panelEl?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const originLeft = rect.left;
    const originTop = rect.top;

    const onMove = (moveEvent: MouseEvent) => {
      // Recover from the button being released outside the browser viewport
      // (Interrogator finding, 2026-09-11 round 2): no `mouseup` is ever
      // delivered to the page in that case, so without this the drag would
      // otherwise keep chasing the cursor indefinitely. `buttons` reflects
      // which buttons are held as of THIS event — 0 means the primary button
      // is already up.
      if (moveEvent.buttons === 0) {
        onUp();
        return;
      }
      const left = originLeft + (moveEvent.clientX - startX);
      const top = originTop + (moveEvent.clientY - startY);
      setDragPosition(clampPanelPosition(left, top, window.innerWidth, window.innerHeight));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragListenersRef.current = null;
      setDragPosition((current) => {
        if (current) writeStoredPanelPosition(authUser?.id, current);
        return current;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    dragListenersRef.current = { onMove, onUp };
  };

  // Keyboard equivalent for drag, plus a way back for a panel dragged
  // somewhere inconvenient (Interrogator finding, 2026-09-11 round 2 — "no
  // keyboard equivalent for drag, and no reset-position control"): arrow keys
  // nudge the panel while the header has focus, and Home resets it to the
  // default Zendesk-stack-relative placement.
  const PANEL_NUDGE_PX = 20;
  const handleHeaderKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.key === 'Home') {
      e.preventDefault();
      setDragPosition(null);
      try {
        window.localStorage.removeItem(panelPositionStorageKey(authUser?.id));
      } catch {
        // Ignore — the reset still applies to this session either way.
      }
      return;
    }
    const deltas: Partial<Record<string, [number, number]>> = {
      ArrowUp: [0, -PANEL_NUDGE_PX],
      ArrowDown: [0, PANEL_NUDGE_PX],
      ArrowLeft: [-PANEL_NUDGE_PX, 0],
      ArrowRight: [PANEL_NUDGE_PX, 0],
    };
    const delta = deltas[e.key];
    if (!delta) return;
    e.preventDefault();
    // Nudges from the current dragPosition state when there is one, not a
    // fresh DOM read — consecutive key presses must accumulate even before
    // React has re-rendered the previous nudge's inline style. Only the
    // very first nudge (never dragged before) needs the panel's actual
    // rendered rect, since there's no absolute left/top yet to nudge from.
    const base = dragPosition ?? document.getElementById(PANEL_ID)?.getBoundingClientRect();
    if (!base) return;
    const next = clampPanelPosition(base.left + delta[0], base.top + delta[1], window.innerWidth, window.innerHeight);
    setDragPosition(next);
    writeStoredPanelPosition(authUser?.id, next);
  };

  // `chatId`/`transcript` are read inside async callbacks (send, retry,
  // escalate, poll) that must always see the LATEST value, not the one
  // captured when the callback/effect was created — refs mirror the
  // corresponding state on every render for that reason.
  const chatIdRef = useRef<string | undefined>(undefined);
  const [chatId, setChatIdState] = useState<string | undefined>(undefined);
  const setChatId = (id: string | undefined) => {
    chatIdRef.current = id;
    setChatIdState(id);
  };

  // Marks the chat seen (Carlos, 9/11) whenever the panel is open and its
  // content changes — same trigger shape as the old local-ref version this
  // replaces, just persisting server-side instead of in memory. Never fires
  // while minimized, so nothing here can clear the badge without the
  // customer actually having the panel open.
  const markSeenSeqRef = useRef(0);
  useEffect(() => {
    if (!open || !chatId) return;
    // Ordering guard (Interrogator finding, 2026-09-11 round 2 —
    // "markChatSeen has no staleness/ordering guard"): this effect can
    // re-fire before a prior call's response lands (e.g. two poll ticks each
    // landing a new turn close together). Every call sets a fresh, real
    // `lastSeenAt` server-side, so two in-flight calls resolving out of order
    // could otherwise regress `lastSeenAt` to the earlier value. Only the
    // most recently ISSUED call's response is applied.
    const seq = ++markSeenSeqRef.current;
    supportAgentApi
      .markChatSeen(chatId)
      .then((chat) => {
        if (seq !== markSeenSeqRef.current) return;
        setLastSeenAt(chat.lastSeenAt);
      })
      .catch(() => {
        // Best-effort — a failed markSeen just means the badge might
        // reappear on the next reopen; nothing else depends on this.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chatId, transcript.length, status]);

  // Cursor for the poll below — the last turn's `at` as OF THE LAST SERVER
  // RESPONSE (a send, an escalate, or a prior poll), never the optimistic
  // customer turn's client-generated timestamp appended in handleSend. Using
  // the optimistic turn's `at` would leave the cursor at client time if that
  // send then failed, which a client clock running ahead of the server could
  // turn into "poll never sees anything new" — see the doc comment on the
  // poll effect below for the fuller race this, `sendingRef`/`escalatingRef`,
  // and `knownAtsRef` together close.
  const lastServerAtRef = useRef<string | undefined>(undefined);
  // Every turn `at` currently in `transcript` — lets the poll below merge in
  // only turns it hasn't already rendered, instead of blindly appending
  // (which would duplicate a turn a send response already delivered).
  const knownAtsRef = useRef<Set<string>>(new Set());
  const transcriptRef = useRef<ChatTurn[]>([]);
  useEffect(() => {
    transcriptRef.current = transcript;
    knownAtsRef.current = new Set(transcript.map((turn) => turn.at));
  }, [transcript]);
  // Mirror `sending`/`escalating` for the poll's interval closure, which is
  // only re-created on [open, chatId] — without these, the poll would act on
  // whichever sending/escalating value was current when the effect last ran,
  // not the value at tick time.
  const sendingRef = useRef(false);
  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);
  const escalatingRef = useRef(false);
  useEffect(() => {
    escalatingRef.current = escalating;
  }, [escalating]);
  // A resolve can take up to 30s (the archive call) and the poll below keeps
  // running the whole time (Carlos, 9/11 — it must, to catch a resolve that
  // happens some OTHER way while minimized). Mirrors `resolving` for the same
  // reason as sendingRef/escalatingRef above (Interrogator finding,
  // 2026-09-11 round 2 — "refreshChat has no guard against a resolve in
  // flight"): resolving via the header X doesn't reset chatId (only "New
  // chat" does), so a poll response in flight when resolveChat() lands could
  // otherwise setStatus back to 'escalated'/'ai' right after the chat
  // resolved.
  const resolvingRef = useRef(false);
  // For sendToApi's status-rank guard below (Pollen review, round 3).
  const statusRef = useRef<ChatStatus>('ai');
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  // The body of the last customer message attempted — Retry resends this
  // exact text rather than whatever the input box currently holds.
  const pendingBodyRef = useRef('');
  // Files that go with the pending send (chat attachments, Michael 10/14).
  const pendingAttachmentsRef = useRef<ChatAttachmentInput[] | undefined>(undefined);

  // Race-guard: bumped on every send and on "New chat". A response only
  // applies if it's still the most recently issued request.
  const requestSeqRef = useRef(0);

  const transcriptEndRef = useRef<HTMLDivElement | null>(null);

  // Return focus to the launcher when the panel closes (Escape, header X, or
  // the Fab itself), instead of dropping it on <body>.
  const fabRef = useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open && wasOpenRef.current) fabRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  // Restore-on-mount (Carlos, 9/9): a page refresh must not lose a live
  // chat. Runs once, regardless of `open` — the panel can be closed on
  // mount and still needs to know it has a chat, so "Talk to a person"/etc.
  // and the next open reflect it immediately rather than waiting for the
  // customer to send a message first. The server is the only source of
  // truth here; nothing is read from localStorage.
  useEffect(() => {
    // Pollen review, round 3 ("Restore-on-mount clobbers a newer chat"): a
    // slow getCurrentChat() landing after the customer already sent a
    // message or hit "New chat" must not overwrite that in-progress chat
    // with the stale restored one. `chatIdRef` moving off its mount-time
    // `undefined` means a send/escalate already produced a real chat id;
    // `requestSeqRef` moving means one STARTED but hasn't resolved yet
    // (e.g. a send still in flight) — either one is reason enough to skip.
    const seqAtMount = requestSeqRef.current;
    supportAgentApi
      .getCurrentChat()
      .then((chat) => {
        if (!chat) return;
        if (requestSeqRef.current !== seqAtMount || chatIdRef.current !== undefined) return;
        setChatId(chat._id);
        setStatus(chat.status);
        setTranscript(chat.turns);
        lastServerAtRef.current = chat.turns[chat.turns.length - 1]?.at;
        // Seed from the SERVER's own last-seen state (Carlos, 9/11), not
        // "everything in this restore is seen" — `lastSeenAt` reflects
        // whenever the customer last actually had the panel open (possibly
        // in an earlier session, possibly never), so anything that landed
        // after it correctly still counts as unread even across a refresh.
        setLastSeenAt(chat.lastSeenAt);
        setEscalatedAt(chat.escalation?.at);
        setResolvedAt(chat.resolvedAt);
        setResolvedBy(chat.resolvedBy);
        applyFeedback(chat);
      })
      .catch(() => {
        // Best-effort — a failed restore just leaves the widget fresh,
        // same as a customer with no chat at all.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches the current chat (a human reply, or a status change) and merges
  // in only what's new — shared by the 5s poll below AND fired immediately
  // whenever the panel opens with a chat already loaded (mount-restore or a
  // prior send), so a chat the inactivity sweeper resolved in the
  // background shows its closed footer as soon as it's reopened, not after
  // waiting out the first poll tick (Carlos, 9/9).
  //
  // Skips entirely while a send/escalate is in flight (checked again after
  // the await, since one can start while this call is out) — otherwise a
  // slow send (the Lambda drafts with a real model call, easily several
  // seconds) races a tick that lands in between: it would append the same
  // customer turn the send's own response is about to deliver (a visible
  // duplicate), and if its response arrives AFTER the send replaces
  // `transcript` wholesale, its turns — read with a cursor from BEFORE the
  // send — would get permanently re-appended on top of the fresher,
  // complete list. Cursored on `lastServerAtRef` (the last turn's `at` from
  // a server response), not `transcript`'s last entry, which may be the
  // client-timestamped optimistic customer turn — see that ref's doc
  // comment. Turns are merged by `at` (`knownAtsRef`) rather than blindly
  // appended, so a turn a send/escalate response already delivered is never
  // duplicated even on a tick that wasn't skipped.
  // Guards against two overlapping refreshChat calls (Pollen review, finding
  // #6) — a reopen fires refreshChat() directly, which can otherwise land
  // while the 5s poll's own tick is still awaiting getChat with the same
  // `since` cursor. `knownAtsRef` only catches up to a merged turn once the
  // resulting `transcript` state commits and its effect re-runs, so a second
  // in-flight call can still see the stale set and re-append the same turns.
  const refreshInFlightRef = useRef(false);

  // Live socket (live-chat-socket.ts), wanted while a chat exists and isn't
  // resolved. `liveConnected` only slows the fallback poll; the doorbell and
  // typing handlers are registered below refreshChat.
  const liveSocketWanted = !!chatId && status !== 'resolved';
  const liveConnected = useLiveChatSocket(liveSocketWanted);
  const [staffTyping, setStaffTyping] = useState<{ name: string } | null>(null);
  const staffTypingTimerRef = useRef<number | null>(null);
  const clearStaffTyping = () => {
    if (staffTypingTimerRef.current !== null) {
      window.clearTimeout(staffTypingTimerRef.current);
      staffTypingTimerRef.current = null;
    }
    setStaffTyping(null);
  };

  // Keeps the transcript's end in view (declared here: it follows staffTyping too).
  useEffect(() => {
    if (!open) return;
    // jsdom doesn't implement scrollIntoView — guard rather than polyfill.
    transcriptEndRef.current?.scrollIntoView?.({ block: 'nearest' });
    // A resolve arriving via poll/socket with no new turn mounts the resolved
    // line and the feedback card below the fold; scroll them into view too.
    // 'nearest' keeps this a no-op when the end is already visible
    // (Michael, 10/20). Support's typing dots too (Michael, 10/26: they
    // mounted below the fold, so "Derek is typing" never showed).
  }, [transcript, sending, open, status, feedbackEligible, feedback, !!staffTyping]);

  // A chat_update doorbell that rang while a refresh, send, escalate or
  // resolve was in flight (Interrogator, #560 review): the in-flight call's
  // response was read before whatever rang, so it is kept and answered with
  // ONE more refresh once the call ends, like the app provider's
  // `_refreshAgain`. Otherwise a staff reply in that window waited for the
  // 30 s fallback poll.
  const refreshAgainRef = useRef(false);
  const refreshBusy = () =>
    sendingRef.current || escalatingRef.current || resolvingRef.current || refreshInFlightRef.current;

  const refreshChat = () => {
    const currentChatId = chatIdRef.current;
    // A resolved chat is no longer polled or on the socket, but a doorbell
    // kept during the refresh that brought the resolve in (refreshAgainRef)
    // would still queue one more getChat here; its pre-submit snapshot
    // (no `feedback` yet) could then land after the customer's feedback
    // submit and blank the locked card. Nothing new can arrive for a
    // resolved chat, so drop it.
    if (!currentChatId || statusRef.current === 'resolved' || refreshBusy()) return;
    refreshInFlightRef.current = true;
    const since = lastServerAtRef.current;
    // Wrapped like the typing note: a throw or an empty answer becomes a
    // skipped refresh, so the `finally` below always frees the next one (#585
    // pipeline 610: a mocked getChat returning nothing threw from the timer).
    Promise.resolve()
      .then(() => supportAgentApi.getChat(currentChatId, since))
      .then((chat) => {
        if (!chat) return;
        if (chatIdRef.current !== currentChatId) return; // "New chat" happened while this was in flight
        if (sendingRef.current || escalatingRef.current || resolvingRef.current) return; // a send/escalate/resolve started while this call was out
        // Same status-rank guard as sendToApi (Interrogator finding,
        // 2026-09-11 round 2): a resolve via the header X doesn't reset
        // chatId (only "New chat" does), and this poll keeps running through
        // the up-to-30s resolve call, so a response that started before the
        // resolve landed but arrives after must not revert status backward.
        if (CHAT_STATUS_RANK[chat.status] < CHAT_STATUS_RANK[statusRef.current]) return;
        const newTurns = chat.turns.filter((turn) => !knownAtsRef.current.has(turn.at));
        if (newTurns.length > 0) {
          setTranscript((prev) => [...prev, ...newTurns]);
          lastServerAtRef.current = newTurns[newTurns.length - 1].at;
          // The reply the dots were promising has landed.
          if (newTurns.some((turn) => turn.role === 'human')) clearStaffTyping();
        }
        setStatus(chat.status);
        // Synchronously too: `.finally` below runs runPendingRefresh before
        // React commits this update, and its resolved gate reads the ref.
        statusRef.current = chat.status;
        setEscalatedAt(chat.escalation?.at);
        setResolvedAt(chat.resolvedAt);
        setResolvedBy(chat.resolvedBy);
        applyFeedback(chat);
      })
      .catch(() => {
        // Best-effort — a transient failure just tries again next tick.
      })
      .finally(() => {
        refreshInFlightRef.current = false;
        runPendingRefresh();
      });
  };

  const runPendingRefresh = () => {
    if (!refreshAgainRef.current || refreshBusy()) return;
    refreshAgainRef.current = false;
    refreshChat();
  };

  // Tracks the LAST `open` value this effect saw, specifically to detect a
  // genuine closed->open transition (a reopen) — not every render where
  // `chatId` merely changes while the panel was already open (e.g. right
  // after the first send sets it), which must NOT trigger a redundant
  // immediate refresh on top of the response `sendToApi` just applied.
  const wasOpenForRefreshRef = useRef(false);
  useEffect(() => {
    // Nothing to poll or refresh for a chat that's already resolved — also
    // avoids a pointless (and racy) network call firing in the same tick as
    // the "reopening a resolved chat starts fresh" effect below, which is
    // about to discard this chatId anyway. `open` is deliberately NOT part of
    // this gate (Carlos, 9/11): the poll must keep running while the panel is
    // MINIMIZED too, or a human reply after escalation (or the chat
    // resolving in the background) would never reach the minimized Fab's
    // badge.
    if (!chatId || status === 'resolved') {
      wasOpenForRefreshRef.current = open;
      return undefined;
    }
    if (!wasOpenForRefreshRef.current && open) {
      refreshChat(); // just reopened (or opening for the first time with a mount-restored chat)
    }
    wasOpenForRefreshRef.current = open;
    const interval = window.setInterval(
      refreshChat,
      liveConnected ? CHAT_POLL_FALLBACK_INTERVAL_MS : CHAT_POLL_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chatId, status, liveConnected]);

  // Live updates (live-chat-socket.ts): the shared socket is held open while
  // this chat is unresolved — minimized too, for the same reason as the
  // poll above — and chat_update for it is the poll's own refresh, now.
  useLiveChatEvent(liveSocketWanted, 'chat_update', (event) => {
    if (event.chatId !== chatIdRef.current) return;
    if (refreshBusy()) {
      refreshAgainRef.current = true;
      return;
    }
    refreshChat();
  });
  useLiveChatEvent(liveSocketWanted, 'chat_typing', (event) => {
    if (event.chatId !== chatIdRef.current) return;
    if (!event.typing) {
      clearStaffTyping();
      return;
    }
    // A typing note stamped before the reply we already show is a late
    // arrival (the server relays it after the reply's own doorbell): not
    // dots for a message that has landed.
    const lastHumanAt = transcript.reduce<string | undefined>(
      (latest, turn) => (turn.role === 'human' && (!latest || turn.at > latest) ? turn.at : latest),
      undefined,
    );
    if (event.at && lastHumanAt && new Date(event.at).getTime() <= new Date(lastHumanAt).getTime()) return;
    const name = typeof event.name === 'string' && event.name.trim() ? event.name.trim() : AI_CHAT_STAFF_NAME;
    setStaffTyping({ name });
    if (staffTypingTimerRef.current !== null) window.clearTimeout(staffTypingTimerRef.current);
    staffTypingTimerRef.current = window.setTimeout(() => {
      staffTypingTimerRef.current = null;
      setStaffTyping(null);
    }, AI_CHAT_STAFF_TYPING_TTL_MS);
  });
  // Nobody types into a chat that isn't escalated; and a new chat starts clean.
  useEffect(() => {
    if (status !== 'escalated') clearStaffTyping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, chatId]);
  useEffect(() => () => clearStaffTyping(), []);

  // The server only enforces this cap while status is 'ai' (chat.service.ts
  // postMessage) — an escalated chat past 30 turns must keep its reply box,
  // since a person is talking to the customer, not the drafter.
  const atLimit = status === 'ai' && transcript.length >= CHAT_MAX_TURNS;
  const closedReason: ClosedReason | null = status === 'resolved' ? 'resolved' : atLimit ? 'limit' : null;
  // Chat attachments (Michael, 10/14): files are only offered, and only
  // accepted by the server, once a person has the chat; anything still
  // pending is dropped when the chat leaves that state or changes.
  const uploads = usePendingAttachments({ chatId, enabled: status === 'escalated' && !closedReason });

  // The history-limit state only means anything while the AI is the one
  // answering (the API applies its cap only to 'ai' chats). If the chat
  // leaves 'ai' by any route — this tab's escalate, another tab's, a poll
  // that lands 'escalated' or 'resolved' — release the composer, otherwise a
  // human conversation could be left locked behind an AI-only limit
  // (Interrogator review, 9/11).
  useEffect(() => {
    if (status !== 'ai') setHistoryLimit(false);
  }, [status]);
  // Minimized Fab appearance (Carlos, 9/11): a chat already resolved AND
  // already seen is, for display purposes, the same as no chat at all — it
  // will auto-reset the moment it's reopened anyway (see the effect above),
  // so there's nothing left worth badging.
  const hasVisibleLiveChat = Boolean(chatId) && (status !== 'resolved' || resolvedUnseen);

  // A signed file url lasts 15 minutes; past that, refetch the chat and swap
  // in the fresh urls before opening the file.
  const refreshAttachment = async (attachment: ChatAttachment): Promise<ChatAttachment | undefined> => {
    const id = chatIdRef.current;
    if (!id) return undefined;
    try {
      const chat = await supportAgentApi.getChat(id);
      const fresh = new Map(chat.turns.flatMap((turn) => turn.attachments ?? []).map((a) => [a.key, a]));
      setTranscript((prev) =>
        prev.map((turn) =>
          turn.attachments?.length
            ? { ...turn, attachments: turn.attachments.map((a) => fresh.get(a.key) ?? a) }
            : turn,
        ),
      );
      return fresh.get(attachment.key);
    } catch {
      return undefined;
    }
  };

  const sendToApi = async () => {
    const seq = ++requestSeqRef.current;
    setSending(true);
    setSendError(null);
    setSendRateLimited(false);
    try {
      const chat = await supportAgentApi.postChatMessage({
        chatId: chatIdRef.current,
        ...(pendingBodyRef.current ? { body: pendingBodyRef.current } : {}),
        ...(pendingAttachmentsRef.current?.length ? { attachments: pendingAttachmentsRef.current } : {}),
      });
      if (seq !== requestSeqRef.current) return; // a reset/newer send happened while this was in flight
      // Ignore a response that would move status BACKWARD (Pollen review,
      // round 3): an escalate that started while this send was in flight
      // (or resolved out of order) may have already landed 'escalated' or
      // even 'resolved' — a stale 'ai' from this send must not undo it.
      if (CHAT_STATUS_RANK[chat.status] < CHAT_STATUS_RANK[statusRef.current]) return;
      setChatId(chat._id);
      setStatus(chat.status);
      setTranscript(chat.turns);
      setEscalatedAt(chat.escalation?.at);
      setResolvedAt(chat.resolvedAt);
      setResolvedBy(chat.resolvedBy);
      applyFeedback(chat);
      lastServerAtRef.current = chat.turns[chat.turns.length - 1]?.at;
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      if ((err as { response?: { data?: { message?: unknown } } })?.response?.data?.message === AI_CHAT_HISTORY_LIMIT_CODE) {
        // The server saved nothing for this message: drop the optimistic
        // customer bubble, hand the text back, and switch to the terminal
        // state. No Retry — see AI_CHAT_HISTORY_LIMIT_COPY.
        const body = pendingBodyRef.current;
        setTranscript((prev) => {
          const last = prev[prev.length - 1];
          return last?.role === 'customer' && last.body === body ? prev.slice(0, -1) : prev;
        });
        setInput(body);
        setHistoryLimit(true);
        return;
      }
      const rateLimited = err?.response?.status === 429;
      setSendRateLimited(rateLimited);
      setSendError(rateLimited ? AI_CHAT_RATE_LIMITED_COPY : AI_CHAT_ERROR_COPY);
    } finally {
      if (seq === requestSeqRef.current) setSending(false);
    }
  };

  // "Customer is typing" for the Chats inbox (Michael, 10/10), on AI chats
  // too (Michael, 10/13: support can watch a chat before stepping in), so
  // only a resolved chat is skipped (the server answers 409 for it, and the
  // input is gone by then anyway). The first keystroke sends it, then at
  // most one every few seconds; clearing the box or leaving sends
  // typing:false, and the message itself clears it server-side.
  // Fire-and-forget, like the inbox's own typing note.
  const customerTypingRef = useRef<{ chatId: string | null; lastSentAt: number }>({ chatId: null, lastSentAt: 0 });
  const sendCustomerTyping = (id: string, typing: boolean) => {
    customerTypingRef.current = typing ? { chatId: id, lastSentAt: Date.now() } : { chatId: null, lastSentAt: 0 };
    Promise.resolve()
      .then(() => supportAgentApi.setCustomerTyping(id, typing))
      .catch((err) => console.warn('[live-chat] setCustomerTyping failed:', err?.message ?? err));
  };
  const stopCustomerTyping = () => {
    const { chatId: typingFor } = customerTypingRef.current;
    if (typingFor) sendCustomerTyping(typingFor, false);
  };
  const handleInputChange = (value: string) => {
    setInput(value);
    const id = chatIdRef.current;
    if (!id || statusRef.current === 'resolved') return;
    if (!value.trim()) {
      stopCustomerTyping();
      return;
    }
    const { chatId: typingFor, lastSentAt } = customerTypingRef.current;
    if (typingFor !== id || Date.now() - lastSentAt >= CUSTOMER_TYPING_SEND_INTERVAL_MS) {
      sendCustomerTyping(id, true);
    }
  };
  useEffect(() => () => stopCustomerTyping(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const trimmed = input.trim();
    // Files ride only on an escalated chat (the hook drops them otherwise).
    const files = status === 'escalated' ? uploads.ready : [];
    if ((!trimmed && files.length === 0) || uploads.blocked || sending || status === 'resolved' || historyLimit) return;
    setValidationError(null);

    // atLimit already disables the input and Send; this is the keyboard path's guard.
    if (atLimit) return;
    if (trimmed.length > CHAT_MAX_BODY_CHARS) {
      setValidationError(`That message is too long — the limit is ${CHAT_MAX_BODY_CHARS.toLocaleString()} characters.`);
      return;
    }

    // Sent, so no longer typing (Michael, 10/23: the inbox kept "Customer is
    // typing" up after the message — a last typing note could land after it).
    stopCustomerTyping();
    pendingBodyRef.current = trimmed;
    pendingAttachmentsRef.current = files.length ? files : undefined;
    setTranscript((prev) => [
      ...prev,
      { role: 'customer', body: trimmed, at: new Date().toISOString(), ...(files.length ? { attachments: files } : {}) },
    ]);
    setInput('');
    uploads.clear();
    // The message itself clears "Customer is typing" server-side.
    customerTypingRef.current = { chatId: null, lastSentAt: 0 };
    void sendToApi();
  };

  const handleRetry = () => {
    // A send error left on a chat that resolved meanwhile has nothing to
    // retry (Michael, 10/20): the server refuses messages to a resolved
    // chat, and the retry's seq bump would discard a feedback submit in
    // flight. The alert itself is hidden on a resolved chat too.
    if (statusRef.current === 'resolved') return;
    void sendToApi();
  };

  // `sending` added to the bail-out (Pollen review, round 3 — "'Talk to a
  // person' is enabled during a send"): the button is also disabled while
  // sending below, but this guards the handler itself the same way
  // handleSend guards against its own disabled conditions, in case
  // something other than a click ever calls it directly.
  const handleEscalate = async () => {
    if (!chatIdRef.current || escalating || sending || status !== 'ai') return;
    // Staleness guard (Pollen review, finding #3), keyed on chatId rather than
    // requestSeqRef — a send legitimately stays in flight alongside an
    // escalate (see CHAT_STATUS_RANK above) so sharing the send's own
    // sequence counter would wrongly discard a still-pending escalate the
    // moment a second send starts. Only "New chat" (which clears chatId) can
    // invalidate this: without the guard, a stale escalate response landing
    // after that reset would revive the old chat's status/transcript.
    const escalatingChatId = chatIdRef.current;
    setEscalating(true);
    setEscalateError(null);
    try {
      const chat = await supportAgentApi.escalateChat(escalatingChatId);
      if (chatIdRef.current !== escalatingChatId) return;
      setStatus(chat.status);
      setTranscript(chat.turns);
      setEscalatedAt(chat.escalation?.at);
      lastServerAtRef.current = chat.turns[chat.turns.length - 1]?.at;
      // A person is on it now; the composer reopens for the human conversation.
      setHistoryLimit(false);
    } catch (err) {
      if (chatIdRef.current !== escalatingChatId) return;
      setEscalateError(AI_CHAT_ESCALATE_ERROR_COPY);
    } finally {
      if (chatIdRef.current === escalatingChatId) setEscalating(false);
    }
  };

  const requestConfirmHuman = () => {
    if (pendingConfirm !== null) return;
    setShowConfirmHuman(true);
  };
  const handleConfirmHumanCancel = () => setShowConfirmHuman(false);
  const handleConfirmHumanYes = () => {
    setShowConfirmHuman(false);
    void handleEscalate();
  };
  // Closes a stale confirm sheet if the chat stops being eligible for
  // escalation while it's open (e.g. the inactivity sweep resolves
  // it, another tab/poll already escalated it, or — Interrogator review,
  // PR #559 — the keyboard can reach the composer and Send behind the
  // sheet's pointer-events-only backdrop, starting a send while the sheet is
  // still up) — handleEscalate's own guard would otherwise just silently
  // no-op on "Yes" (it bails on `sending` same as the other conditions here),
  // leaving the sheet open with no way to tell why the click did nothing.
  useEffect(() => {
    if (showConfirmHuman && (!chatId || status !== 'ai' || sending)) setShowConfirmHuman(false);
  }, [showConfirmHuman, chatId, status, sending]);

  const handleNewChat = () => {
    stopCustomerTyping();
    uploads.clear();
    pendingAttachmentsRef.current = undefined;
    requestSeqRef.current += 1; // invalidate any in-flight request
    setChatId(undefined);
    setStatus('ai');
    lastServerAtRef.current = undefined;
    setTranscript([]);
    setInput('');
    setSending(false);
    setValidationError(null);
    setSendError(null);
    setHistoryLimit(false);
    setEscalating(false);
    setEscalateError(null);
    setLastSeenAt(undefined);
    setEscalatedAt(undefined);
    setResolvedAt(undefined);
    setResolvedBy(undefined);
    setFeedbackEligible(false);
    setFeedback(undefined);
    setShowConfirmHuman(false);
  };

  /**
   * Feedback card: sends the answers for the current chat and applies the
   * server's response (the locked card renders from `feedback`). A 409 —
   * already sent, or no longer eligible — re-reads the chat so the card
   * shows whatever the server has instead of an error; anything else is
   * thrown for the card to show its retry message.
   *
   * Staleness is keyed on chatId, like handleEscalate, not on the send
   * counter (Michael, 10/20): a feedback submit is independent of message
   * sends, and a dropped success would leave the form looking unsent while
   * the server has the answers. Only "New chat" (which clears chatId) can
   * invalidate it.
   */
  const handleSubmitFeedback = async (input: ChatFeedbackInput): Promise<ChatFeedback | undefined> => {
    const id = chatIdRef.current;
    if (!id) return undefined;
    try {
      const chat = await supportAgentApi.submitChatFeedback(id, input);
      if (chatIdRef.current !== id) return undefined;
      applyFeedback(chat);
      return chat.feedback;
    } catch (err: any) {
      if (chatIdRef.current !== id) return undefined;
      if (err?.response?.status === 409) {
        const chat = await supportAgentApi.getChat(id);
        if (chatIdRef.current !== id) return undefined;
        applyFeedback(chat);
        return chat.feedback;
      }
      throw err;
    }
  };

  // Reopening a resolved chat starts fresh (Carlos, 9/9) — a customer who
  // closes the panel after a resolution and reopens it later should land on
  // an empty chat, not the old transcript with a disabled input. Only fires
  // on OPEN, not on `status` itself flipping to 'resolved' while already
  // open (that's still shown live, with the "Start a new chat" button above,
  // not auto-reset out from under whoever is reading it).
  //
  // Skipped when `resolvedUnseen` (Carlos, 9/11 — "resolved while
  // minimized"): if the chat resolved in the background while the panel was
  // minimized (`resolvedAt` after `lastSeenAt`), the customer hasn't seen the
  // resolved notice yet — reopening must show the transcript and the closed
  // footer, not silently wipe it out from under them.
  useEffect(() => {
    if (open && status === 'resolved' && !resolvedUnseen) {
      handleNewChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Enter sends, Shift+Enter inserts a newline — the usual chat convention.
    // Skip the Enter that commits an IME composition (CJK input) so a
    // half-typed message isn't sent.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    // A confirm sheet now renders INSIDE the panel (Carlos, 10/7), so Escape
    // reaches this same handler whether or not one is open — route it to
    // whichever sheet is up instead of minimizing underneath it, same
    // in-flight guard as a backdrop click (see ai-chat-escalation.tsx). With
    // nothing open, Escape minimizes immediately, same as the bottom Fab
    // (Carlos, 9/9's original ask, still true now that the Fab itself only
    // minimizes; 9/11).
    if (showConfirmHuman) {
      if (!escalating) handleConfirmHumanCancel();
      return;
    }
    if (pendingConfirm === 'newChat') {
      if (!resolving) handleKeepOpen();
      return;
    }
    if (pendingConfirm === 'close') {
      if (!resolving) dismissCloseConfirm();
      return;
    }
    setOpen(false);
  };

  // Ending a live (not yet resolved) chat asks first, so a customer can't
  // lose an in-progress conversation with one accidental click — from the
  // header X (Carlos, 9/9: end the conversation and close the panel) or from
  // "New chat" (Carlos, 9/11: end this one and start a fresh one, panel
  // stays open). `pendingConfirm` tracks which one opened the dialog, since
  // confirming/canceling does something different afterward depending on
  // which it was. Nothing to confirm once a chat is resolved, or if one was
  // never started at all.
  const [pendingConfirm, setPendingConfirm] = useState<'close' | 'newChat' | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  useEffect(() => {
    resolvingRef.current = resolving;
  }, [resolving]);
  // A send/escalate/resolve ending is the other moment a kept chat_update
  // doorbell can be answered (see refreshAgainRef); `resolving` is declared
  // just above, which is why this effect lives here and not next to refreshChat.
  useEffect(() => {
    if (!sending && !escalating && !resolving) runPendingRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, escalating, resolving]);
  const needsCloseConfirm = Boolean(chatId) && status !== 'resolved';

  // Only one confirm (the "Talk to a person?" sheet, or whichever
  // pendingConfirm dialog/sheet) can ever be open at once (Carlos, 10/7 —
  // found two stacked after the sheet's backdrop only covered the transcript,
  // leaving the header's other buttons reachable underneath it). Moving the
  // sheets to cover the whole panel (see the Paper render below) already
  // blocks the click that would reach these in the UI; guarding the handlers
  // too makes that invariant hold regardless of how they're called, and is
  // what the stacking-prevention tests exercise directly.
  const requestClose = () => {
    if (showConfirmHuman || pendingConfirm !== null) return;
    if (needsCloseConfirm) {
      setResolveError(null);
      setPendingConfirm('close');
    } else {
      setOpen(false);
    }
  };

  const requestNewChat = () => {
    if (showConfirmHuman || pendingConfirm !== null) return;
    if (needsCloseConfirm) {
      setResolveError(null);
      setPendingConfirm('newChat');
    } else {
      handleNewChat();
    }
  };

  // Canceling means keep the SAME live chat, untouched — for the header X's
  // dialog that still closes the panel (Carlos, 9/9: "Keep open" means keep
  // the CONVERSATION live, not the panel; the panel closes either way, since
  // that's what clicking X already asked for). For "New chat"'s dialog,
  // canceling just dismisses it — the current chat and the open panel are
  // otherwise untouched.
  const handleKeepOpen = () => {
    // Guarded (Pollen review, finding #4): canceling while the resolve is
    // still in flight must not let it appear to have worked — the dialog
    // stays up until the request actually settles.
    if (resolving) return;
    const wasClose = pendingConfirm === 'close';
    setPendingConfirm(null);
    if (wasClose) setOpen(false);
  };

  // Backdrop click / Esc on the close-confirm sheet (Carlos, 10/7) — distinct
  // from the "Keep open" BUTTON above: clicking outside or pressing Esc means
  // the X click itself was a mistake, so only the sheet goes away and the
  // panel stays open untouched. The "Keep open" button's own click keeps
  // that documented close-the-panel behavior unchanged.
  const dismissCloseConfirm = () => {
    if (resolving) return;
    setPendingConfirm(null);
  };

  const handleConfirmResolve = async () => {
    if (resolving) return;
    const wasClose = pendingConfirm === 'close';
    const id = chatIdRef.current;
    const finish = () => {
      setPendingConfirm(null);
      if (wasClose) {
        setOpen(false);
      } else {
        handleNewChat();
      }
    };
    if (!id) {
      finish();
      return;
    }
    setResolving(true);
    try {
      const chat = await supportAgentApi.resolveChat(id);
      setStatus(chat.status);
      setTranscript(chat.turns);
      setResolvedAt(chat.resolvedAt);
      setResolvedBy(chat.resolvedBy);
      applyFeedback(chat);
      // Feedback card (Michael, 10/20): a customer who closes the chat
      // themselves is the one most worth asking, so when the server says
      // the resolved chat is eligible, the panel stays open on the card
      // instead of closing. The X (no chat left to confirm), minimize and
      // "Start a new chat" all still let them leave without answering. A
      // chat the server will not ask about (urgent, etc.) closes as before.
      if (wasClose && chat.feedbackEligible === true) {
        setPendingConfirm(null);
        return;
      }
      if (wasClose) {
        // Mark this resolution as already seen (Carlos, 9/11 — bug: the
        // minimized Fab kept showing the live-chat pill after resolving from
        // here). The panel is about to close (`finish()` below), and the
        // mark-seen effect never fires once `open` is false — so without
        // this, `lastSeenAt` would stay at whatever it was before this
        // resolve, making `resolvedUnseen` wrongly true even though the
        // customer explicitly triggered and watched this resolve happen.
        // Uses the server's own `chat.resolvedAt` (Pollen review, finding
        // #5), not the client clock — a client ahead of server time could
        // otherwise mark the resolve "seen" before its own resolvedAt,
        // making `resolvedUnseen` spuriously true again on the very next
        // restore-on-mount. (Not needed on the "New chat" path —
        // `handleNewChat` inside `finish()` resets `lastSeenAt`/`resolvedAt`
        // to undefined anyway.)
        setLastSeenAt(chat.resolvedAt);
      }
      finish();
    } catch (err) {
      // A failed resolve must not silently proceed — the customer would
      // believe the old chat is done (Pollen review, 9/9) or lose it to a
      // new one they didn't actually confirm. Keep the dialog open so they
      // can retry or cancel.
      setResolveError(AI_CHAT_RESOLVE_ERROR_COPY);
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
      {open && (
        <Paper
          id={PANEL_ID}
          elevation={8}
          role="dialog"
          aria-label={AI_CHAT_TITLE}
          onKeyDown={handlePanelKeyDown}
          sx={{
            position: 'fixed',
            // Undragged: today's placement, tracking the Zendesk stack.
            // Dragged: pinned by left/top instead (Carlos, 9/11), clamped so
            // the whole panel — not just the header — stays on screen.
            ...(dragPosition
              ? { left: dragPosition.left, top: dragPosition.top }
              : { right: EDGE_OFFSET_PX, bottom: fabBottomPx + FAB_SIZE_PX + 16 }),
            width: PANEL_WIDTH_PX,
            maxWidth: `calc(100vw - ${EDGE_OFFSET_PX * 2}px)`,
            height: PANEL_HEIGHT_PX,
            maxHeight: dragPosition
              ? `calc(100vh - ${dragPosition.top}px)`
              : `calc(100vh - ${EDGE_OFFSET_PX + fabBottomPx + FAB_SIZE_PX + 16}px)`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            // Above the app's own AppBar (1100) and Drawer/sidebar (1200) —
            // Carlos, 9/11: dragging the panel over the left nav must not let
            // the nav render on top of it — but still below Modal (1300) so
            // the app's own dialogs stack above the chat.
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Box
            onMouseDown={handleHeaderMouseDown}
            onKeyDown={handleHeaderKeyDown}
            tabIndex={0}
            role="group"
            aria-label="Chat panel position — arrow keys to move, Home to reset"
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'grab',
            }}
          >
            <RobotIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                {AI_CHAT_TITLE}
              </Typography>
            </Box>
            <Tooltip title={status === 'ai' ? 'Talk to a person' : 'Already talking to a person'}>
              <span>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Talk to a person"
                  disabled={!chatId || status !== 'ai' || escalating || sending}
                  onClick={requestConfirmHuman}
                >
                  <SupportAgentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="New chat">
              <span>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="New chat"
                  disabled={transcript.length === 0 && !sending}
                  onClick={requestNewChat}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="End chat">
              <span>
                <IconButton size="small" color="inherit" aria-label="Close chat" onClick={requestClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Relative wrapper so the first-run tour (screens 1-3) can overlay
              everything below the header without covering it, same as the
              design's blue header bar staying visible under the tour card. */}
          <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Live region so replies, the typing indicator, and errors are announced. */}
          <Box
            aria-live="polite"
            sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {transcript.length === 0 && !sending && (
              <AssistantBubble>
                <Typography variant="body2">{AI_CHAT_EMPTY_COPY}</Typography>
              </AssistantBubble>
            )}
            {transcript.length === 0 && !sending && intro.showTourRestartLink && (
              <Button
                size="small"
                onClick={intro.restartTour}
                sx={{
                  alignSelf: 'flex-start',
                  color: '#2563A6',
                  fontSize: 13,
                  fontWeight: 500,
                  textDecoration: 'underline',
                  textTransform: 'none',
                  '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
                }}
              >
                {AI_CHAT_INTRO_SHOW_AGAIN}
              </Button>
            )}
            {transcript.map((turn, i) =>
              turn.role === 'customer' ? (
                <CustomerBubble key={i} body={turn.body} attachments={turn.attachments} onRefreshAttachment={refreshAttachment} />
              ) : (
                <AgentTurn key={i} turn={turn} onRefreshAttachment={refreshAttachment} />
              ),
            )}
            {/* Only while status is 'ai' (Pollen review, round 3): an
                escalated chat never gets an AI reply — the drafter isn't
                called for it server-side — so showing "typing" during a
                send there would promise a reply that's never coming. */}
            {sending && status === 'ai' && <TypingIndicator />}
            {staffTyping && status === 'escalated' && <TypingIndicator label={staffTypingCopy(staffTyping.name)} />}
            {/* Not on a resolved chat (Michael, 10/20): a stale send error
                would sit above the feedback card offering a Retry that
                can't succeed. */}
            {sendError && status !== 'resolved' && (
              <Alert
                severity={sendRateLimited ? 'warning' : 'error'}
                action={
                  // No Retry for a rate limit (Pollen review, finding #9) —
                  // an immediate retry would just re-trip the same window;
                  // the customer needs to wait, not tap a button that fails
                  // again instantly.
                  !sendRateLimited && (
                    <Button color="inherit" size="small" disabled={sending} onClick={handleRetry}>
                      Retry
                    </Button>
                  )
                }
              >
                {sendError}
              </Alert>
            )}
            {historyLimit && status === 'ai' && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" disabled={escalating || !chatId} onClick={requestConfirmHuman}>
                    Talk to a person
                  </Button>
                }
              >
                {AI_CHAT_HISTORY_LIMIT_COPY}
              </Alert>
            )}
            {escalateError && <Alert severity="error">{escalateError}</Alert>}
            {status === 'escalated' &&
              !transcript.some((turn) => turn.role === 'human') &&
              !transcript.some((turn) => turn.urgent) && (
                <HandoffBanner openState={supportHoursState().openState} />
              )}
            {status === 'resolved' && (
              <Alert severity="success">
                <Box component="strong" sx={{ fontWeight: 600 }}>
                  {resolvedCopy(resolvedBy, resolvedAt)}
                </Box>
              </Alert>
            )}
            {status === 'resolved' && (
              <AiChatFeedback
                eligible={feedbackEligible}
                feedback={feedback}
                supportJoined={escalatedAt !== undefined}
                onSubmit={handleSubmitFeedback}
              />
            )}
            <div ref={transcriptEndRef} />
          </Box>

          {validationError && (
            <Alert severity="warning" sx={{ mx: 2, mb: 1 }} onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}
          {uploads.error && (
            <Alert severity="warning" sx={{ mx: 2, mb: 1 }} data-testid="ai-chat-attachment-error">
              {uploads.error}
            </Alert>
          )}

          <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
            {closedReason ? (
              <ClosedFooter reason={closedReason} onStartNewChat={handleNewChat} />
            ) : (
              <>
                <PendingAttachmentList items={uploads.items} onRemove={uploads.remove} onRetry={uploads.retry} disabled={sending} />
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  {status === 'escalated' && (
                    <AttachmentPickerButton disabled={sending || historyLimit} onFiles={uploads.add} />
                  )}
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    autoFocus
                    placeholder="Type your message…"
                    value={input}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    disabled={sending || historyLimit}
                    inputRef={composerInputRef}
                    inputProps={{ 'aria-label': 'Your message' }}
                  />
                  <IconButton
                    color="primary"
                    aria-label="Send"
                    disabled={(!input.trim() && uploads.ready.length === 0) || uploads.blocked || sending || historyLimit}
                    onClick={handleSend}
                  >
                    <SendIcon />
                  </IconButton>
                </Stack>
              </>
            )}
          </Box>

          {intro.showTour && (
            <TourOverlay step={intro.tourStep} onNext={handleTourNext} onBack={intro.tourBack} onSkip={intro.tourSkip} />
          )}
          </Box>
          {/* Rendered as a direct child of the Paper (not the content wrapper
              above) so the backdrop covers the WHOLE panel, header included
              (Carlos, 10/7 — a sheet that only dimmed the transcript left the
              header's headset/New chat/X buttons clickable underneath it,
              letting a second confirm stack on top of the first). Paper's own
              `position: fixed` already makes it the containing block for an
              absolutely-positioned direct child, and its overflow: hidden
              clips the sheet to the same rounded corners. */}
          {showConfirmHuman && (
            <ConfirmHumanSheet
              openState={supportHoursState().openState}
              confirming={escalating}
              onConfirm={handleConfirmHumanYes}
              onCancel={handleConfirmHumanCancel}
            />
          )}
          {pendingConfirm === 'newChat' && (
            <ConfirmRestartSheet
              status={status}
              confirming={resolving}
              error={resolveError}
              onConfirm={() => void handleConfirmResolve()}
              onCancel={handleKeepOpen}
            />
          )}
          {pendingConfirm === 'close' && (
            <ConfirmResolveSheet
              resolving={resolving}
              error={resolveError}
              onResolve={() => void handleConfirmResolve()}
              onKeepOpen={handleKeepOpen}
              onDismiss={dismissCloseConfirm}
            />
          )}
        </Paper>
      )}

      <AnnouncementDialog
        open={intro.showAnnouncement}
        onTryItNow={handleTryItNow}
        onDismiss={intro.dismissAnnouncement}
        zIndexAboveZendesk={
          zendesk.zIndex !== null && zendesk.zIndex > MUI_DIALOG_Z_INDEX ? zendesk.zIndex + 1 : undefined
        }
      />

      {intro.showNudge && (
        <NudgeLabel
          bottomPx={fabBottomPx}
          fabSizePx={FAB_SIZE_PX}
          rightPx={EDGE_OFFSET_PX}
          gapPx={STACK_GAP_PX}
          onDismiss={intro.dismissNudge}
        />
      )}

      {/* Pulsing ring and red dot, only while the nudge shows (design: nudge.markup.html). */}
      {intro.showNudge && (
        <Box
          aria-hidden
          data-testid="ai-chat-fab-pulse-ring"
          sx={{
            position: 'fixed',
            right: EDGE_OFFSET_PX,
            bottom: fabBottomPx,
            width: FAB_SIZE_PX,
            height: FAB_SIZE_PX,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            zIndex: (theme) => theme.zIndex.speedDial - 1,
            transition: 'bottom 200ms ease',
            pointerEvents: 'none',
            animation: 'pomPulse 2s ease-out infinite',
            '@keyframes pomPulse': POM_PULSE_KEYFRAMES,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      )}
      {intro.showNudge && (
        <Box
          aria-hidden
          data-testid="ai-chat-fab-nudge-dot"
          sx={{
            position: 'fixed',
            right: EDGE_OFFSET_PX + NUDGE_DOT_INSET_PX,
            bottom: fabBottomPx + FAB_SIZE_PX - NUDGE_DOT_INSET_PX - NUDGE_DOT_SIZE_PX,
            width: NUDGE_DOT_SIZE_PX,
            height: NUDGE_DOT_SIZE_PX,
            borderRadius: '50%',
            bgcolor: '#E4572E',
            border: '2px solid #FFFFFF',
            zIndex: (theme) => theme.zIndex.speedDial + 1,
            transition: 'bottom 200ms ease',
            pointerEvents: 'none',
          }}
        />
      )}

      <Fab
        ref={fabRef}
        color="primary"
        variant={!open && hasVisibleLiveChat ? 'extended' : 'circular'}
        aria-label={
          open
            ? 'Minimize chat'
            : !hasVisibleLiveChat
              ? 'Open support chat'
              : unreadCount > 0
                ? `Support chat, ${unreadCount} new`
                : 'Support chat'
        }
        aria-expanded={open}
        aria-controls={open ? PANEL_ID : undefined}
        // Minimizing (open -> not open) never asks for confirmation — the
        // chat stays live in the background either way (Carlos, 9/11). The
        // header X inside the panel is the only path that can resolve/end a
        // chat; this button just toggles visibility.
        onClick={() => (open ? setOpen(false) : setOpen(true))}
        sx={{
          position: 'fixed',
          right: EDGE_OFFSET_PX,
          bottom: fabBottomPx,
          transition: 'bottom 200ms ease',
          zIndex: (theme) => theme.zIndex.speedDial,
        }}
      >
        {open ? (
          <ExpandMoreIcon />
        ) : !hasVisibleLiveChat ? (
          <RobotIcon />
        ) : (
          <>
            <Badge badgeContent={unreadCount} color="error" sx={{ mr: 1 }}>
              <RobotIcon />
            </Badge>
            Support chat
            <ExpandLessIcon sx={{ ml: 0.5 }} />
          </>
        )}
      </Fab>

    </>
  );
};

/**
 * Gate. Returns null when REACT_APP_AI_CHAT_ENABLED is off, so nothing
 * renders and no request can be made (the only thing that runs with the flag
 * off is a side-effect-free store read).
 *
 * A POM Master's own (non-masquerading) session always sees it, same as
 * before this org-level flag existed — this was originally the ONLY gate,
 * back when the widget was POM-Master-only.
 *
 * A masquerading session used to be force-hidden here too (Carlos, 9/9,
 * "must not write into that customer's actual persisted chat"), but Carlos
 * reversed that (9/14): masquerade must now see exactly what the
 * masqueraded customer would see, so it is evaluated by the same
 * `chatSupport.mode` check as any other customer below. `authUser?.pomMaster`
 * is never true for a masqueraded identity (it carries the real customer's
 * own fields), so removing the old early `masquarade` return is sufficient
 * - no extra masquerade-specific branch is needed.
 *
 * Everyone else (including a masquerading session) is evaluated as a real
 * customer: shown only when their org's `chatSupport.mode` setting
 * (Carlos, 9/11 — resolved server-side via GET /v2/settings/me, cascading
 * system → customer → group → team) is `ai_chat_only` or `both`. Default
 * `zendesk_chat_only` means no existing org's customers see this until
 * explicitly flipped over. Held to null while that setting is still
 * loading, so a customer whose org DOES have it on never gets a flash of
 * "no button" before it appears.
 */
export const AiChatWidget: React.FC = () => {
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const { getSetting, isLoading } = useEffectiveSettings();
  if (!isAiChatEnabled()) return null;
  if (authUser?.pomMaster) return <AiChatWidgetInner />;
  if (isLoading) return null;
  const chatMode = getSetting<string>(SETTING.chatSupportMode, 'zendesk_chat_only');
  if (chatMode !== 'ai_chat_only' && chatMode !== 'both') return null;
  return <AiChatWidgetInner />;
};

export default AiChatWidget;
